import { Link } from '@inertiajs/react';

export default function Pagination({
    data,
    showInfo = true,
    showPerPage = false,
    perPageOptions = [15, 25, 50, 100],
    onPerPageChange = null,
    className = ''
}) {
    if (!data || data.last_page <= 1) return null;

    const { current_page, last_page, from, to, total, links, per_page } = data;

    // Generate smart page numbers with ellipsis
    const getPageNumbers = () => {
        const pages = [];
        const showPages = 5; // Max pages to show

        if (last_page <= showPages + 2) {
            // Show all pages if total is small
            for (let i = 1; i <= last_page; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (current_page > 3) {
                pages.push('...');
            }

            // Pages around current
            const start = Math.max(2, current_page - 1);
            const end = Math.min(last_page - 1, current_page + 1);

            for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) {
                    pages.push(i);
                }
            }

            if (current_page < last_page - 2) {
                pages.push('...');
            }

            // Always show last page
            if (!pages.includes(last_page)) {
                pages.push(last_page);
            }
        }

        return pages;
    };

    const getPageUrl = (page) => {
        const link = links.find(l => l.label === String(page));
        return link?.url || null;
    };

    const prevLink = links.find(l => l.label.includes('Previous') || l.label.includes('&laquo;'));
    const nextLink = links.find(l => l.label.includes('Next') || l.label.includes('&raquo;'));

    return (
        <div className={`pagination-wrapper ${className}`}>
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                {/* Info & Per Page */}
                <div className="d-flex align-items-center gap-3">
                    {showInfo && (
                        <span className="pagination-info text-muted">
                            Showing <span className="fw-medium text-dark">{from?.toLocaleString()}</span> to{' '}
                            <span className="fw-medium text-dark">{to?.toLocaleString()}</span> of{' '}
                            <span className="fw-medium text-dark">{total?.toLocaleString()}</span>
                        </span>
                    )}

                    {showPerPage && onPerPageChange && (
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted d-none d-md-inline">Show</span>
                            <select
                                className="form-select form-select-sm pagination-per-page"
                                value={per_page}
                                onChange={(e) => onPerPageChange(e.target.value)}
                            >
                                {perPageOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                <nav aria-label="Pagination">
                    <ul className="pagination mb-0">
                        {/* Previous Button */}
                        <li className={`page-item ${!prevLink?.url ? 'disabled' : ''}`}>
                            {prevLink?.url ? (
                                <Link
                                    href={prevLink.url}
                                    className="page-link page-link-nav"
                                    preserveScroll
                                    aria-label="Previous"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                    <span className="d-none d-md-inline ms-1">Previous</span>
                                </Link>
                            ) : (
                                <span className="page-link page-link-nav">
                                    <i className="bi bi-chevron-left"></i>
                                    <span className="d-none d-md-inline ms-1">Previous</span>
                                </span>
                            )}
                        </li>

                        {/* Page Numbers - Hidden on mobile */}
                        <div className="d-none d-sm-flex">
                            {getPageNumbers().map((page, index) => (
                                page === '...' ? (
                                    <li key={`ellipsis-${index}`} className="page-item disabled">
                                        <span className="page-link page-link-ellipsis">...</span>
                                    </li>
                                ) : (
                                    <li key={page} className={`page-item ${current_page === page ? 'active' : ''}`}>
                                        {current_page === page ? (
                                            <span className="page-link">{page}</span>
                                        ) : (
                                            <Link
                                                href={getPageUrl(page)}
                                                className="page-link"
                                                preserveScroll
                                            >
                                                {page}
                                            </Link>
                                        )}
                                    </li>
                                )
                            ))}
                        </div>

                        {/* Mobile Page Indicator */}
                        <li className="page-item d-sm-none">
                            <span className="page-link page-link-mobile">
                                {current_page} / {last_page}
                            </span>
                        </li>

                        {/* Next Button */}
                        <li className={`page-item ${!nextLink?.url ? 'disabled' : ''}`}>
                            {nextLink?.url ? (
                                <Link
                                    href={nextLink.url}
                                    className="page-link page-link-nav"
                                    preserveScroll
                                    aria-label="Next"
                                >
                                    <span className="d-none d-md-inline me-1">Next</span>
                                    <i className="bi bi-chevron-right"></i>
                                </Link>
                            ) : (
                                <span className="page-link page-link-nav">
                                    <span className="d-none d-md-inline me-1">Next</span>
                                    <i className="bi bi-chevron-right"></i>
                                </span>
                            )}
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    );
}
